import { AgGridReact } from "ag-grid-react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import { useHistory, useParams } from "react-router";
import { FilterChipComponentRef } from "entity-app/shared-components/log-render-components/filter-chip-component";
import { AppContext } from "context/AppProvider";
import { applyMaterialLogFilters } from "utils/ag-grid-programmatic-filtering";
import { FeatureTypes } from "entity-app/constants";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";

import { EmailInviteStatus, ProjectPermissionEnum } from "constants/index";
import { UtilColumnConfig } from "components/column-config-views/column-config-util";
import { resolveEffectiveColumnConfig } from "components/column-config-views/master-column-config";
import type { TSaveColumnState } from "components/column-config-views";
import { getRowId } from "pages/submittals-log-page/utils";
import { updateGridSelectionOnFilter } from "utils/utils";
import { InfoFeatureType } from "change-events/change-event-polling";
import { excelStyles } from "components/excel-ag-grid-export/excel-style";
import { useFeatureLogV2InlineEdit } from "entity-app/hooks/feature-log-v2-inline-edit";
import { useLocationBreakdownV2Structure } from "hooks/location-breakdown-v2-structure";
import CiqAgGrid from "components/ciq-ag-grid";
import RequestedDurationDetails from "components/request-durations/requested-duration-details";
import RequestDurationsSingle from "components/request-durations/request-durations-single";
import { getMaterialRequestDurationTitle } from "components/request-durations/material-request-duration-button/use-material-request-duration-button";
import { Modal } from "antd";
import {
  backwardCompatibilityForLinkedTasksFilter,
  captureExpandedGroupRoutes,
  enforceSingleRowGroupSelection,
  isGroupOpenByDefaultFromRoutes,
  restoreExpandedGroupRoutes
} from "utils/grid-utils";
import useFeatureLogNavWriter from "hooks/use-feature-log-nav-writer";
import { useMaterialLogData } from "./hooks/material-feature-data";
import { useMaterialLogAgGrid } from "./hooks/material-ag-grid-data";
import { getMaterialIdColumnWidth } from "./material-id-column-width";
import MaterialHeaderItems from "./components/header-items";
import { TMaterialLog } from "./model";
import "./material-log.scss";
import { ColDef, ColGroupDef, SideBarDef } from "ag-grid-enterprise";
import PdfViewerSpecPopup from "./components/pdf-viewer-spec-popup";
import {
  generateSavedColConfigsForColIds,
  getInterleavedDateBlockColumnIds,
  getSidebarColumnLayout,
  isMilestoneOrDurationColumnId,
  PRESERVE_MILESTONE_NAMED_COLUMN_IDS
} from "entity-app/shared-components/entity-milestones-duration-columns/feature-date-block-columns";
import GroupBySelector from "./components/group-by-selector";
import { getConditionalFormattingHeaderMenuItems } from "components/conditional-formatting/conditional-formatting-header-menu";
import type { ConditionalFormattingRule } from "components/conditional-formatting";
import { useConditionalFormatting } from "components/conditional-formatting/use-conditional-formatting";
import { RiskLevelType } from "components/date-block/models";

const featureKey = "materials"; // URL

interface MaterialLogProps {
  pageSidebar: {
    sidebarAction: (params: {
      feature: string;
      action: "open" | "close";
      itemDetailsToSet: any | null;
    }) => void;
    sidebarMetadata: {
      feature: "comments" | string;
      itemDetails: any | null;
    } | null;
    isSidebarOpen: boolean;
  };
}

function MaterialLog({ pageSidebar }: Readonly<MaterialLogProps>) {
  const { sidebarAction, sidebarMetadata, isSidebarOpen } = pageSidebar;
  const { itemDetails } = sidebarMetadata || {};
  const history = useHistory();
  const gridRef = useRef<AgGridReact<any>>(null);
  const {
    projectDetails,
    tokenContents,
    isFeatureFlagEnabled,
    gqlClientForProject,
    loggedInUserProjectProfileResponse,
    isProductEntitlementsDataAvailable,
    isProjectArchived
  } = useContext(ProjectContext);

  const [requestingDurationsForFeature, setRequestingDurationsForFeature] =
    useState<any | null>(null);

  const [pdfSpecsInfo, setPdfSpecsInfo] = useState<{
    ai_insights: any;
    specSection: {
      number: string;
      name: string;
    };
    title: string;
  } | null>(null);

  const cannotCreateMaterial = isPermissionNotGrantted(
    ProjectPermissionEnum.CreateMaterial,
    tokenContents?.role ?? ""
  );

  const canInlineEditMaterialGeneralInfoAndParticipants: any =
    !isPermissionNotGrantted(
      ProjectPermissionEnum.EditMaterialGeneralInfoAndParticipants,
      tokenContents?.role ?? ""
    );

  const canViewLinkingPage = !isPermissionNotGrantted(
    ProjectPermissionEnum.ViewLinkingPage,
    tokenContents?.role ?? ""
  );

  const canInlineEditMaterialCharacteristics: any = !isPermissionNotGrantted(
    ProjectPermissionEnum.EditMaterialCharacteristics,
    tokenContents?.role ?? ""
  );

  const canInlineEditMaterialDBOffset: any = !isPermissionNotGrantted(
    ProjectPermissionEnum.EditMaterialDBOffset,
    tokenContents?.role ?? ""
  );

  const cannotChangeActualDate = isPermissionNotGrantted(
    ProjectPermissionEnum.changeMaterialActualDate,
    tokenContents?.role ?? ""
  );

  const canSetLBSForMaterial = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanSetLBSForMaterial,
    tokenContents?.role ?? ""
  );

  const canRequestLeadTime = !isPermissionNotGrantted(
    ProjectPermissionEnum.RequestLeadTime,
    tokenContents?.role ?? ""
  );

  const isRequestLeadTimeFeatureEnabled =
    isFeatureFlagEnabled("REQUEST_LEAD_TIME");

  const isMaterialExtractionEnabled =
    isFeatureFlagEnabled("MATERIAL_EXTRACTION") && !cannotCreateMaterial;

  const isAiMaterialInsightsDashboardEnabled = isFeatureFlagEnabled(
    "AI_MATERIAL_INSIGHTS"
  );

  const isMaterialCsiClassificationEnabled = isFeatureFlagEnabled(
    "MATERIAL_CSI_CLASSIFICATION"
  );

  const { projectId } = useParams() as any;
  const conditionalFormattingRulesRef = useRef<ConditionalFormattingRule[]>([]);
  const pageData: any = useMaterialLogData({
    gridRef,
    projectId,
    getConditionalsFormatting: () => conditionalFormattingRulesRef.current
  });

  const {
    isLBSFeatureEnabled,
    LBSOptionsMaster,
    LBSDataInTreeFormat,
    handleOnFilterOpen
  } = useLocationBreakdownV2Structure({
    entityList: pageData.entityList,
    projectId,
    gridRef,
    hasPermissionToSetLBSForMaterial: canSetLBSForMaterial
  });

  const agGridData = useMaterialLogAgGrid({
    featureKey,
    milestonesColumns: pageData.milestonesColumns,
    cannotWriteAccessOfMaterial: cannotCreateMaterial,
    materialHeaderMap: pageData.materialHeaderMap,
    isLBSFeatureEnabled,
    canSetLBSForMaterial,
    isMaterialCsiClassificationEnabled
  });
  const {
    conditionalFormattingRules,
    conditionalFormattingDefaultColDef,
    conditionalFormattingExcelStyles,
    setConditionalFormattingRules
  } = useConditionalFormatting({
    gridRef,
    defaultColDef: agGridData.defaultColDef
  });
  conditionalFormattingRulesRef.current = conditionalFormattingRules;

  const mergedExcelStyles = useMemo(
    () => [...excelStyles, ...conditionalFormattingExcelStyles],
    [conditionalFormattingExcelStyles]
  );

  const { onCellEditRequest } = useFeatureLogV2InlineEdit({
    refetchListData: pageData.fetchLogData,
    infoFeatureType: InfoFeatureType.material
  });
  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const { materialListFilter } = useContext(AppContext);

  const navWriter = useFeatureLogNavWriter({
    gridRef,
    feature: "materials",
    projectId
  });

  const [isFirstDataRendered, setIsFirstDataRendered] = useState(false);
  const isFirstDataRenderedRef = useRef(false);
  isFirstDataRenderedRef.current = isFirstDataRendered;
  const onSaveColumnStateRef = useRef(pageData.onSaveColumnState);
  onSaveColumnStateRef.current = pageData.onSaveColumnState;
  const [searchText, setSearchText] = useState("");
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const [selectedRows, setSelectedRows] = useState<Array<TMaterialLog> | null>(
    null
  );
  const expandedGroupRoutesRef = useRef<Set<string>>(new Set());
  // Default/master column config must apply once. Entity refreshes rebuild
  // milestone column defs and would otherwise re-run apply with stale user config.
  const hasAppliedDefaultColumnConfigRef = useRef(false);

  const [itemSourceRowIndexForSidepanel, setItemSourceRowIndexForSidepanel] =
    useState<number | null>(null);

  const applySidebarTreeLayout = useCallback(() => {
    if (!gridRef.current?.api) return;
    const milestonesCount =
      pageData.maxNumberOfMilestones?.milestonesCount ?? 0;
    if (milestonesCount <= 0) return;
    const { sidebarColumnLayout } = getSidebarColumnLayout(
      pageData.maxNumberOfMilestones?.milestonesCount ?? null,
      pageData.maxNumberOfMilestones?.milestoneIndicesWithOffset
    );
    if (typeof gridRef.current.api.getToolPanelInstance !== "function") return;
    const columnsToolPanel =
      gridRef.current.api.getToolPanelInstance?.("columns");
    if (!columnsToolPanel) return;

    const existingLayout =
      (gridRef.current.api.getColumnDefs() as (ColDef | ColGroupDef)[]) || [];
    const filteredExistingLayout = existingLayout.filter(
      (item: ColDef | ColGroupDef) => {
        const colId = (item as ColDef).colId;
        if (!colId) return true;
        if (colId === "_milestones" || colId === "_durations") return false;
        // Remove both legacy and new date-block leaf columns before applying tree layout
        if (colId.startsWith("milestone_")) return false; // legacy
        if (colId.startsWith("offset_")) return false; // legacy
        if (
          colId.includes("milestone_") &&
          !PRESERVE_MILESTONE_NAMED_COLUMN_IDS.has(colId)
        )
          return false; // name_milestone_, planned_milestone_, etc
        if (colId.includes("offset_")) return false; // name_offset_, duration_offset_
        return true;
      }
    );
    columnsToolPanel.setColumnLayout([
      ...filteredExistingLayout,
      ...sidebarColumnLayout
    ]);
    const toolPanelState = (columnsToolPanel as any).getState?.();
    if (toolPanelState) {
      (columnsToolPanel as any).setState?.({
        ...toolPanelState,
        expandedGroupIds: (toolPanelState as any).expandedGroupIds?.filter(
          (id: string) => id !== "milestones_group" && id !== "durations_group"
        )
      });
    }
    (columnsToolPanel as any).collapseColumnGroups?.([
      "milestones_group",
      "durations_group"
    ]);
  }, [pageData.maxNumberOfMilestones]);

  const afterApplySavedViewCallback = useCallback(() => {
    setTimeout(() => {
      applySidebarTreeLayout();
    }, 0);
  }, [applySidebarTreeLayout]);

  const milestoneOffsetIndicesKey = pageData.maxNumberOfMilestones
    ?.milestoneIndicesWithOffset
    ? Array.from(
        pageData.maxNumberOfMilestones.milestoneIndicesWithOffset as Set<number>
      )
        .sort((a: number, b: number) => a - b)
        .join(",")
    : "";
  useEffect(() => {
    if (!isFirstDataRendered) return;
    const timer = setTimeout(() => applySidebarTreeLayout(), 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFirstDataRendered,
    pageData.maxNumberOfMilestones?.milestonesCount,
    milestoneOffsetIndicesKey
  ]);

  // The ID cell grows a hover-revealed "View Specs" button on rows that carry
  // AI insights, and the default 80px column cannot hold it beside the id. Size
  // to the ids actually loaded (see getMaterialIdColumnWidth) rather than to a
  // fixed number, which either wastes space or squeezes the id as ids grow.
  const hasAiInsightRows = useMemo(
    () =>
      isAiMaterialInsightsDashboardEnabled &&
      (pageData.entityList ?? []).some(
        (material: any) => Object.keys(material?.ai_insights ?? {}).length > 0
      ),
    [isAiMaterialInsightsDashboardEnabled, pageData.entityList]
  );
  const hasSizedIdColumnForAiInsightsRef = useRef(false);

  useEffect(() => {
    // Gate on isFirstDataRendered so this lands *after* the saved/master column
    // config has been applied — otherwise applyColumnState overwrites us.
    if (!isFirstDataRendered || !hasAiInsightRows) return;
    if (hasSizedIdColumnForAiInsightsRef.current) return;
    const api = gridRef.current?.api;
    if (!api) return;
    hasSizedIdColumnForAiInsightsRef.current = true;

    // Measured here rather than during render: by now the grid has painted and
    // any saved column width has been restored, so the id link's real font is
    // on the page and getColumnState reports the width actually in effect.
    const targetWidth = getMaterialIdColumnWidth(pageData.entityList);
    const currentWidth =
      (api.getColumnState?.() ?? []).find((col) => col.colId === "material_id")
        ?.width ?? 0;

    // Only ever grow. A wider width — saved or dragged — is the user's and is
    // left alone; a narrower one predates this content, because ids grow over a
    // project's life (165 today, 10000 later) and a width saved back when they
    // were short would clip them now.
    if (currentWidth >= targetWidth) return;

    api.setColumnWidths?.([{ key: "material_id", newWidth: targetWidth }]);
  }, [isFirstDataRendered, hasAiInsightRows, pageData.entityList]);

  const onSearch = useCallback((text: string) => {
    setSearchText(text);
    gridRef.current?.api?.setGridOption("quickFilterText", text);
  }, []);

  const applyResolvedMaterialColumnConfig = useCallback(
    (effectiveConfig: TSaveColumnState) => {
      if (materialListFilter || !gridRef.current) return;

      const columnList = effectiveConfig.columnList as any[];
      if (!columnList?.length) return;

      let sortedColumnConfig = [...columnList].sort(
        (a: any, b: any) => (a?.order ?? 0) - (b?.order ?? 0)
      );

      const orderedDateBlockColIds = getInterleavedDateBlockColumnIds(
        pageData.milestonesColumns?.columnDefsForDateBlock || []
      );
      const hasMilestoneDurationCols = orderedDateBlockColIds.length > 0;

      if (hasMilestoneDurationCols) {
        const milestonesColumnVisibility =
          sortedColumnConfig.find(
            (col: any) => col?.field?.name === "_milestones"
          )?.visibility ?? false;
        const durationsColumnVisibility =
          sortedColumnConfig.find(
            (col: any) => col?.field?.name === "_durations"
          )?.visibility ?? false;

        const baseConfig = sortedColumnConfig.filter((x: any) => {
          const name = x?.field?.name;
          if (name === "_milestones" || name === "_durations") return false;
          if (isMilestoneOrDurationColumnId(name)) return false;
          return true;
        });

        const existingLeaf = sortedColumnConfig.filter((x: any) =>
          isMilestoneOrDurationColumnId(x?.field?.name)
        );

        const maxOrder =
          sortedColumnConfig.reduce((acc: number, col: any) => {
            const order = typeof col?.order === "number" ? col.order : 0;
            return Math.max(acc, order);
          }, 0) || 0;

        const leafConfig = generateSavedColConfigsForColIds(
          orderedDateBlockColIds,
          maxOrder,
          {
            milestonesColumnVisibility,
            durationsColumnVisibility
          },
          existingLeaf
        );

        sortedColumnConfig = [...baseConfig, ...leafConfig];
      }

      if (isMaterialCsiClassificationEnabled) {
        const hasDivisionConfig = sortedColumnConfig.some(
          (col: any) => col?.field?.name === "division"
        );
        if (!hasDivisionConfig) {
          const maxOrder =
            sortedColumnConfig.reduce((acc: number, col: any) => {
              const order = typeof col?.order === "number" ? col.order : 0;
              return Math.max(acc, order);
            }, 0) || 0;
          const specSectionCol = sortedColumnConfig.find(
            (col: any) => col?.field?.name === "custom_spec_section"
          );
          const divisionOrder = specSectionCol?.order
            ? specSectionCol.order + 0.5
            : maxOrder + 1;

          sortedColumnConfig = [
            ...sortedColumnConfig,
            {
              field: {
                name: "division",
                range: null,
                width: null,
                filter: null,
                agFilter: null,
                sortOrder: null
              },
              order: divisionOrder,
              visibility: true
            }
          ];
        }
      }

      const updatedColumnConfig =
        backwardCompatibilityForLinkedTasksFilter(sortedColumnConfig);

      UtilColumnConfig.setFilterStateFromData({
        columnStates: {
          ...effectiveConfig,
          columnList: updatedColumnConfig
        },
        gridRef: {
          api: gridRef.current.api
        },
        restoreRowGrouping: isMaterialCsiClassificationEnabled
      });
      setSearchText(effectiveConfig.quickSearch ?? "");
      const nextRules = effectiveConfig.conditionals_formatting ?? [];
      conditionalFormattingRulesRef.current = nextRules;
      setConditionalFormattingRules(nextRules);
    },
    [
      materialListFilter,
      pageData.milestonesColumns?.columnDefsForDateBlock,
      isMaterialCsiClassificationEnabled,
      setConditionalFormattingRules
    ]
  );

  const onConditionalFormattingRulesChange = useCallback(
    (rules: ConditionalFormattingRule[]) => {
      conditionalFormattingRulesRef.current = rules;
      setConditionalFormattingRules(rules);
      if (isFirstDataRenderedRef.current && !materialListFilter) {
        onSaveColumnStateRef.current();
      }
    },
    [materialListFilter, setConditionalFormattingRules]
  );

  const applyDefaultColumnConfig = useCallback((): boolean => {
    if (materialListFilter) {
      return false;
    }
    if (
      pageData.userColumnConfigLoading ||
      pageData.masterColumnConfigLoading
    ) {
      return false;
    }

    const effectiveConfig = resolveEffectiveColumnConfig(
      pageData.savedColumns?.material_column_config,
      pageData.masterColumnConfig?.master_column_config,
      FeatureTypes.MATERIALS
    );

    if (!effectiveConfig?.columnList?.length) {
      // Config queries finished but nothing to apply — treat as done.
      return true;
    }

    applyResolvedMaterialColumnConfig(effectiveConfig);
    return true;
  }, [
    applyResolvedMaterialColumnConfig,
    materialListFilter,
    pageData.masterColumnConfig?.master_column_config,
    pageData.masterColumnConfigLoading,
    pageData.savedColumns?.material_column_config,
    pageData.userColumnConfigLoading
  ]);

  useEffect(() => {
    if (materialListFilter || hasAppliedDefaultColumnConfigRef.current) {
      return;
    }
    if (!pageData.isColumnConfigReady) {
      return;
    }
    if (!pageData.entityListLoadedOnce || !gridRef.current?.api) {
      return;
    }
    if (applyDefaultColumnConfig()) {
      hasAppliedDefaultColumnConfigRef.current = true;
      setTimeout(() => applySidebarTreeLayout(), 0);
    }
  }, [
    pageData.isColumnConfigReady,
    pageData.entityListLoadedOnce,
    pageData.masterColumnConfig?.master_column_config,
    pageData.savedColumns,
    materialListFilter,
    applyDefaultColumnConfig,
    applySidebarTreeLayout
  ]);

  const onFirstDataRendered = useCallback(() => {
    if (materialListFilter) {
      const loggedInUserCompany =
        loggedInUserProjectProfileResponse?.data?.subscription_users[0]?.user
          .project_users[0]?.subscription_vendor?.name;
      applyMaterialLogFilters({
        gridRef,
        materialListFilter,
        additionalData: { loggedInUserCompany }
      });
    } else if (!hasAppliedDefaultColumnConfigRef.current) {
      if (applyDefaultColumnConfig()) {
        hasAppliedDefaultColumnConfigRef.current = true;
      }
    }

    // Append sidebar milestone/duration columns to current Columns ToolPanel layout
    // Use the number of *visible* milestone groups so unchecked ones are not shown
    const shouldApplySidebarLayout =
      (pageData.maxNumberOfMilestones?.milestonesCount ?? 0) > 0;

    let sidebarColumnLayout: (ColDef | ColGroupDef)[] = [];
    if (shouldApplySidebarLayout) {
      let milestonesCountForSidebar: number | null = null;
      if (gridRef.current?.api) {
        const columnDefs = gridRef.current.api.getColumnDefs() as (
          | ColDef
          | ColGroupDef
        )[];
        const visibleMilestoneNameCols = columnDefs.filter((col) => {
          const colId = (col as ColDef).colId;
          if (!colId) return false;
          // Prefer new keys, but tolerate legacy keys if present in saved configs
          if (
            !colId.startsWith("name_milestone_") &&
            !colId.startsWith("milestone_name_")
          ) {
            return false;
          }
          // ag-Grid uses `hide` to control visibility on colDefs
          return !(col as ColDef).hide;
        });
        if (visibleMilestoneNameCols.length) {
          milestonesCountForSidebar = visibleMilestoneNameCols.length;
        }
      }

      ({ sidebarColumnLayout } = getSidebarColumnLayout(
        milestonesCountForSidebar,
        pageData.maxNumberOfMilestones?.milestoneIndicesWithOffset
      ));
    }

    // Apply tree layout; run after setFilterStateFromData (if called) so it isn't overwritten
    setTimeout(() => {
      if (typeof gridRef.current?.api?.getToolPanelInstance !== "function")
        return;
      const columnsToolPanel =
        gridRef.current?.api?.getToolPanelInstance?.("columns");
      if (columnsToolPanel && gridRef.current?.api) {
        const existingLayout =
          (gridRef.current.api.getColumnDefs() as (ColDef | ColGroupDef)[]) ||
          [];
        const filteredExistingLayout = existingLayout.filter(
          (item: ColDef | ColGroupDef) => {
            const colId = (item as ColDef).colId;
            if (!colId) return true;
            if (colId === "_milestones" || colId === "_durations") return false;
            if (colId.startsWith("milestone_")) return false; // legacy
            if (colId.startsWith("offset_")) return false; // legacy
            if (
              colId.includes("milestone_") &&
              !PRESERVE_MILESTONE_NAMED_COLUMN_IDS.has(colId)
            )
              return false; // new
            if (colId.includes("offset_")) return false; // new
            return true;
          }
        );
        columnsToolPanel.setColumnLayout(
          sidebarColumnLayout.length
            ? [...filteredExistingLayout, ...sidebarColumnLayout]
            : filteredExistingLayout
        );
        const toolPanelState = (columnsToolPanel as any).getState?.();
        if (toolPanelState) {
          (columnsToolPanel as any).setState?.({
            ...toolPanelState,
            expandedGroupIds: (toolPanelState as any).expandedGroupIds?.filter(
              (id: string) =>
                id !== "milestones_group" && id !== "durations_group"
            )
          });
        }
        if (sidebarColumnLayout.length) {
          (columnsToolPanel as any).collapseColumnGroups?.([
            "milestones_group",
            "durations_group"
          ]);
        }
      }
    }, 0);

    setTimeout(() => {
      // NOTE: To load initial data once in onFilterApplied method
      setIsFirstDataRendered(true);
    }, 500);

    navWriter.onFirstDataRendered();
  }, [
    materialListFilter,
    loggedInUserProjectProfileResponse,
    pageData,
    applyDefaultColumnConfig,
    navWriter
  ]);

  const onEditCell = useCallback(
    (params: any) => {
      gridRef?.current?.api?.setFocusedCell(params.rowIndex, params.colKey);
      gridRef?.current?.api?.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.colKey
      });
    },
    [gridRef]
  );

  const onRequestDurationClick = (material: any) => {
    setRequestingDurationsForFeature(material);
  };

  const requestDurationModalTitle = useMemo(() => {
    if (!requestingDurationsForFeature) {
      return "Request Lead Time/Status";
    }

    return getMaterialRequestDurationTitle(requestingDurationsForFeature);
  }, [requestingDurationsForFeature]);

  const onLinkClick = (material: any) => {
    history.push(
      `/project/${projectId}/schedule/material-linking?materialId=${material.id}`
    );
  };

  const onCommentClick = ({
    sourceRowIndex
  }: {
    sourceRowIndex: number | undefined | null;
  }) => {
    if (sourceRowIndex === undefined || sourceRowIndex === null) return;
    sidebarAction({
      feature: "comments",
      action: "open",
      itemDetailsToSet: pageData.entityList[sourceRowIndex]
    });
    setItemSourceRowIndexForSidepanel(sourceRowIndex);
  };

  useEffect(() => {
    if (!isSidebarOpen) return;
    if (itemSourceRowIndexForSidepanel !== null) {
      const material = pageData.entityList[itemSourceRowIndexForSidepanel];
      if (!material) return;
      sidebarAction({
        feature: "comments",
        action: "open",
        itemDetailsToSet: material
      });
    }
  }, [
    itemSourceRowIndexForSidepanel,
    pageData.entityList,
    sidebarAction,
    isSidebarOpen
  ]);

  useEffect(() => {
    if (!isSidebarOpen) {
      setItemSourceRowIndexForSidepanel(null);
    }
  }, [isSidebarOpen]);

  const rowClassRules = useMemo(
    () => ({
      "selected-item-for-panel": (params: any) => {
        if (params.node?.group) return false;
        return params.data?.id === itemDetails?.id;
      }
    }),
    [itemDetails?.id]
  );

  const isGroupOpenByDefault = useCallback(
    (params: any) =>
      isGroupOpenByDefaultFromRoutes(params, expandedGroupRoutesRef.current),
    []
  );

  const icons = useMemo<{
    [key: string]: ((...args: any[]) => any) | string;
  }>(
    () => ({
      group_by: '<span class="ag-icon ag-icon-group"></span>'
    }),
    []
  );

  const extendToolPanels = useMemo<SideBarDef["toolPanels"]>(
    () =>
      isMaterialCsiClassificationEnabled
        ? [
            {
              id: "group_by",
              labelDefault: "Group By",
              labelKey: "group_by",
              iconKey: "group_by",
              toolPanel: GroupBySelector,
              width: 300,
              minWidth: 250,
              maxWidth: 350
            }
          ]
        : [],
    [isMaterialCsiClassificationEnabled]
  );

  if (
    (materialListFilter && loggedInUserProjectProfileResponse?.loading) ||
    !isProductEntitlementsDataAvailable
  ) {
    return <div />;
  }

  return (
    <div
      className="flex flex-col h-full w-full"
      data-testid="material-feature-log-page"
    >
      <MaterialHeaderItems
        {...{
          selectedRows,
          gridRef,
          pageData,
          agGridData,
          filterChipRef,
          statsData,
          featureKey,
          projectId,
          afterApplySavedViewCallback,
          isMaterialCsiClassificationEnabled,
          searchText,
          onSearch,
          setSearchText,
          getConditionalsFormatting: () =>
            conditionalFormattingRulesRef.current,
          onConditionalsFormattingChange: (
            rules: ConditionalFormattingRule[]
          ) => {
            conditionalFormattingRulesRef.current = rules;
            setConditionalFormattingRules(rules);
          },
          pageSidebarState: {
            isSidebarOpen,
            sidebarAction
          }
        }}
      />

      <CiqAgGrid
        {...{
          onFilterOpened: (event: any) => {
            handleOnFilterOpen(event);
          },
          gridRef,
          columnDefs: agGridData.columnDefs,
          rowData: pageData.entityList,
          defaultColDef: conditionalFormattingDefaultColDef,
          getMainMenuItems: getConditionalFormattingHeaderMenuItems,
          rowHeight: 62,
          getRowId, // After refetch the rowdata ,It help to persist Scroll Bar
          rowSelection: {
            mode: "multiRow",
            selectAll: "filtered",
            groupSelects: "filteredDescendants",
            checkboxLocation: "autoGroupColumn"
          },
          excelStyles: mergedExcelStyles,
          readOnlyEdit: true,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
          onCellEditRequest: (event: any) => {
            onCellEditRequest(event);
          },
          onFirstDataRendered,
          onSortChanged: () => {
            if (isFirstDataRendered && !materialListFilter)
              pageData.onSaveColumnState();
            navWriter.onSortChanged();
          },
          onFilterChanged: (event: any) => {
            filterChipRef.current?.onFilterApplied();
            setStatsData(
              calculateLogPageStats({
                gridRef,
                ListFilter: materialListFilter,
                featureTypeId: FeatureTypes.MATERIALS
              })
            );

            updateGridSelectionOnFilter(event, gridRef);
            if (isFirstDataRendered && !materialListFilter)
              pageData.onSaveColumnState();
            navWriter.onFilterChanged();
          },
          onColumnVisible: (e: any) => {
            if (e.source === "toolPanelUi") {
              pageData.onSaveColumnState();
            }
          },
          onColumnRowGroupChanged: (e: any) => {
            enforceSingleRowGroupSelection({
              api: e?.api,
              fallbackApi: gridRef.current
                ? (gridRef.current as any).api
                : null,
              clearSortForInactiveGroupColIds: ["division", "trade_partner"]
            });
            const rowGroupColumns = e?.api?.getRowGroupColumns?.() ?? [];
            if (!rowGroupColumns.length) {
              expandedGroupRoutesRef.current = new Set();
            } else {
              expandedGroupRoutesRef.current = captureExpandedGroupRoutes(
                e?.api
              );
            }
            if (isFirstDataRendered && !materialListFilter) {
              setTimeout(() => {
                pageData.onSaveColumnState();
              }, 0);
            }
          },
          onRowGroupOpened: (event: any) => {
            expandedGroupRoutesRef.current = captureExpandedGroupRoutes(
              event?.api
            );
          },
          onRowDataUpdated: (event: any) => {
            restoreExpandedGroupRoutes(
              event?.api,
              expandedGroupRoutesRef.current
            );
            navWriter.onModelUpdated();
          },

          onDragStopped: (e) => {
            const classNames = [
              "ag-header-cell-resize",
              "ag-tool-panel-horizontal-resize"
            ];
            if (e.api && !classNames.includes(e.target.className)) {
              pageData.onSaveColumnState();
              setTimeout(() => {
                applySidebarTreeLayout();
              }, 0);
            }
          },
          onToolPanelVisibleChanged: (e: any) => {
            if (e?.key === "columns" && e?.visible) {
              setTimeout(() => applySidebarTreeLayout(), 0);
            }
          },
          onNewColumnsLoaded: () => {
            // ag-grid resyncs the columns tool panel with the (flat) grid
            // columnDefs whenever new columns load — that overwrites our
            // setColumnLayout. Re-apply the tree so it survives data-driven
            // columnDefs updates.
            setTimeout(() => applySidebarTreeLayout(), 0);
          },
          context: {
            conditionalFormattingRules,
            onConditionalFormattingRulesChange,
            conditionalFormattingColumnConfig: {
              risk_level: {
                filterType: "set",
                options: [
                  RiskLevelType.High,
                  RiskLevelType.Medium,
                  RiskLevelType.Low
                ]
              },
              status: {
                filterType: "set",
                options: pageData.statusMilestoneOptions ?? []
              }
            },
            projectDetails,
            onEditCell,
            isGridCellEditable: agGridData.isGridCellEditable,
            refetchLogData: pageData.fetchLogData,
            editPermission: !cannotChangeActualDate,
            canInlineEditMaterialGeneralInfoAndParticipants,
            canInlineEditMaterialCharacteristics,
            canInlineEditDBOffset: canInlineEditMaterialDBOffset,
            cannotChangeActualDate,
            canSetLBSForMaterial,
            LBSOptionsMaster,
            LBSDataInTreeFormat,
            gqlClientForProject,
            material_quantity_unit: pageData.allUnits?.material_quantity_unit,
            isRequestLeadTimeFeatureEnabled,
            isMaterialExtractionEnabled,
            canRequestLeadTime,
            onLinkClick,
            canViewLinkingPage,
            onRequestDurationClick,
            materialHeaderMap: pageData.materialHeaderMap,
            history,
            onCommentClick,
            isProjectArchived: !!isProjectArchived,
            setPdfSpecsInfo,
            isAiMaterialInsightsDashboardEnabled
          },
          onSelectionChanged: () => {
            setSelectedRows(gridRef?.current?.api?.getSelectedRows() || null);
          },
          loading: pageData.entityListLoading,
          onColumnResized: (params: any) => {
            if (params.finished && params.source === "uiColumnResized") {
              // User has finished resizing, do something like save column state
              pageData.onSaveColumnState();
            }
          },
          disableResizingAutoWidth: true,
          rowClassRules,
          autoGroupColumnDef: agGridData.autoGroupColumnDef,
          isGroupOpenByDefault,
          suppressGroupChangesColumnVisibility: "suppressHideOnGroup",
          rowGroupPanelShow: "never",
          // groupLockGroupColumns: -1,
          pivotMode: false,
          pivotPanelShow: "never",
          extendToolPanels,
          icons: icons
        }}
      />

      <Modal
        title={
          <div className="font-semibold text-sm">
            <div>{requestDurationModalTitle}</div>
            <div className="text-xs mt-1 font-medium">
              {requestingDurationsForFeature?.name ||
                requestingDurationsForFeature?.title}
            </div>
          </div>
        }
        data-testid="index-modal-request-duration-single"
        open={
          requestingDurationsForFeature &&
          requestingDurationsForFeature?.invite_status !==
            EmailInviteStatus.REQUEST_SENT
        }
        okText="Send Request"
        styles={{ body: { padding: 0 } }}
        footer={null}
        onCancel={() => {
          setRequestingDurationsForFeature(null);
        }}
        destroyOnHidden
      >
        <RequestDurationsSingle
          onCancel={() => setRequestingDurationsForFeature(null)}
          onComplete={() => {
            setRequestingDurationsForFeature(null);
          }}
          selectedItem={requestingDurationsForFeature}
        />
      </Modal>

      <Modal
        title={
          <div className="font-semibold text-sm">
            <div>{requestDurationModalTitle}</div>
            <div className="text-xs mt-1 font-medium">
              {requestingDurationsForFeature?.name ||
                requestingDurationsForFeature?.title}
            </div>
          </div>
        }
        data-testid="index-modal-requested-duration"
        open={
          requestingDurationsForFeature &&
          requestingDurationsForFeature?.invite_status ===
            EmailInviteStatus.REQUEST_SENT
        }
        styles={{ body: { padding: 0 } }}
        footer={null}
        onCancel={() => {
          setRequestingDurationsForFeature(null);
        }}
        destroyOnHidden
        className="requested-duration-details-dialog"
      >
        <RequestedDurationDetails
          selectedItem={requestingDurationsForFeature}
          onCancel={() => {
            setRequestingDurationsForFeature(null);
          }}
        />
      </Modal>

      {pdfSpecsInfo && (
        <PdfViewerSpecPopup
          title={pdfSpecsInfo.title}
          ai_insights={pdfSpecsInfo.ai_insights}
          specSection={pdfSpecsInfo.specSection}
          materialListFilter={materialListFilter}
          onClose={() => {
            setPdfSpecsInfo(null);
          }}
        />
      )}
    </div>
  );
}

export default MaterialLog;
